import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Eye, EyeOff, MailCheck } from 'lucide-react';
import { brand } from '@shared/brand.js';
import { api } from '../../lib/api.js';
import { useSession } from '../../app/session.jsx';
import { useDocumentTitle, useForm } from '../../app/hooks.js';
import { Button, EmptyState, Field, Input } from '../../components/ui.jsx';

export function AuthLayout({ title, subtitle, children, footer }) {
  return (
    <main className="auth-screen">
      <div className="auth-card">
        <div className="auth-brand">
          <img src="/favicon.svg" alt="" />
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <div className="card auth-panel">{children}</div>
        {footer && <div className="auth-footer">{footer}</div>}
      </div>
    </main>
  );
}

function PasswordField({ label = 'Senha', error, hint, autoComplete, ...props }) {
  const [show, setShow] = useState(false);
  return (
    <Field label={label} error={error} hint={hint}>
      {(a) => (
        <div style={{ position: 'relative' }}>
          <input
            className="input"
            type={show ? 'text' : 'password'}
            autoComplete={autoComplete}
            style={{ paddingRight: 48 }}
            {...a}
            {...props}
          />
          <button
            type="button"
            className="icon-btn"
            style={{ position: 'absolute', right: 0, top: 0 }}
            aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
            aria-pressed={show}
            onClick={() => setShow((s) => !s)}
          >
            {show ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
          </button>
        </div>
      )}
    </Field>
  );
}

function FormError({ error }) {
  if (!error) return null;
  return (
    <div className="alert alert-danger" role="alert">
      {error}
    </div>
  );
}

function safeBack(params) {
  const back = params.get('volta');
  return back && back.startsWith('/') && !back.startsWith('//') ? back : null;
}

export function LoginPage() {
  useDocumentTitle('Entrar');
  const { setMe } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const f = useForm({ email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const me = await api('/api/auth/login', { method: 'POST', body: f.values });
      setMe(me);
      navigate(safeBack(params) ?? (me.households.length ? '/' : '/boas-vindas'), { replace: true });
    } catch (err) {
      f.setErrors(err.fieldErrors ?? {});
      setError(err.status === 400 ? null : err.message);
    } finally {
      setBusy(false);
    }
  };

  const back = params.get('volta');
  return (
    <AuthLayout
      title={`Entrar no ${brand.name}`}
      subtitle={brand.tagline}
      footer={
        <>
          Ainda não tem conta?{' '}
          <Link to={`/cadastrar${back ? `?volta=${encodeURIComponent(back)}` : ''}`}>Criar conta</Link>
        </>
      }
    >
      <form className="form" onSubmit={submit} noValidate>
        <FormError error={error} />
        <Input
          label="E-mail"
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          error={f.errors.email}
          {...f.bind('email')}
        />
        <PasswordField
          autoComplete="current-password"
          required
          error={f.errors.password}
          {...f.bind('password')}
        />
        <Button type="submit" variant="primary" block loading={busy}>
          Entrar
        </Button>
        <Link to="/esqueci-senha" className="small" style={{ justifySelf: 'center' }}>
          Esqueci minha senha
        </Link>
      </form>
    </AuthLayout>
  );
}

export function RegisterPage() {
  useDocumentTitle('Criar conta');
  const { setMe } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const f = useForm({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const me = await api('/api/auth/register', { method: 'POST', body: f.values });
      setMe(me);
      navigate(safeBack(params) ?? '/boas-vindas', { replace: true });
    } catch (err) {
      f.setErrors(err.fieldErrors ?? {});
      setError(err.status === 400 ? null : err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Criar sua conta"
      subtitle="Leva menos de um minuto. Depois você cria o seu lar ou entra num convite."
      footer={
        <>
          Já tem conta?{' '}
          <Link
            to={`/entrar${params.get('volta') ? `?volta=${encodeURIComponent(params.get('volta'))}` : ''}`}
          >
            Entrar
          </Link>
        </>
      }
    >
      <form className="form" onSubmit={submit} noValidate>
        <FormError error={error} />
        <Input
          label="Como quer ser chamado(a)"
          autoComplete="name"
          required
          maxLength={80}
          error={f.errors.name}
          {...f.bind('name')}
        />
        <Input
          label="E-mail"
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          error={f.errors.email}
          {...f.bind('email')}
        />
        <PasswordField
          autoComplete="new-password"
          required
          hint="Pelo menos 8 caracteres."
          error={f.errors.password}
          {...f.bind('password')}
        />
        <Button type="submit" variant="primary" block loading={busy}>
          Criar conta
        </Button>
        <p className="tiny muted" style={{ textAlign: 'center' }}>
          Seus dados ficam visíveis apenas para as pessoas dos lares de que você participar.
        </p>
      </form>
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  useDocumentTitle('Recuperar senha');
  const f = useForm({ email: '' });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/password/forgot', { method: 'POST', body: f.values });
      setSent(true);
    } catch (err) {
      f.setErrors(err.fieldErrors ?? {});
      setError(err.status === 400 ? null : err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Recuperar senha" footer={<Link to="/entrar">Voltar para entrar</Link>}>
      {sent ? (
        <EmptyState icon={MailCheck} title="Confira seu e-mail">
          Se houver uma conta com esse endereço, enviamos um link para criar uma nova senha. Ele vale por 1
          hora.
          {import.meta.env.DEV && (
            <>
              {' '}
              Em desenvolvimento, veja a <Link to="/dev/caixa-de-email">caixa de e-mail local</Link>.
            </>
          )}
        </EmptyState>
      ) : (
        <form className="form" onSubmit={submit} noValidate>
          <p className="muted small">
            Informe o e-mail da sua conta. Vamos enviar um link para você criar uma nova senha.
          </p>
          <FormError error={error} />
          <Input
            label="E-mail"
            type="email"
            autoComplete="email"
            inputMode="email"
            required
            error={f.errors.email}
            {...f.bind('email')}
          />
          <Button type="submit" variant="primary" block loading={busy}>
            Enviar link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  useDocumentTitle('Nova senha');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // O token é lido uma vez e removido da barra de endereço (não fica no histórico).
  const [token] = useState(() => params.get('token') ?? '');
  const [state, setState] = useState(() => (token ? 'checking' : 'invalid'));
  const f = useForm({ password: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.history.replaceState(null, '', '/redefinir-senha');
    if (!token) return;
    api(`/api/auth/password/reset/${encodeURIComponent(token)}`)
      .then(() => setState('valid'))
      .catch(() => setState('invalid'));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.values.password !== f.values.confirm) {
      f.setErrors({ confirm: 'As senhas não conferem.' });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api('/api/auth/password/reset', { method: 'POST', body: { token, password: f.values.password } });
      setState('done');
    } catch (err) {
      f.setErrors(err.fieldErrors ?? {});
      setError(err.status === 400 && err.code === 'validation' ? null : err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Criar nova senha" footer={<Link to="/entrar">Voltar para entrar</Link>}>
      {state === 'checking' && <p className="muted">Verificando o link…</p>}
      {state === 'invalid' && (
        <EmptyState
          title="Link inválido ou expirado"
          action={
            <Link className="btn" to="/esqueci-senha">
              Pedir novo link
            </Link>
          }
        >
          Por segurança, cada link vale por 1 hora e só pode ser usado uma vez.
        </EmptyState>
      )}
      {state === 'done' && (
        <EmptyState
          icon={MailCheck}
          title="Senha alterada"
          action={
            <Button variant="primary" onClick={() => navigate('/entrar')}>
              Entrar
            </Button>
          }
        >
          Por segurança, encerramos as sessões abertas em outros aparelhos.
        </EmptyState>
      )}
      {state === 'valid' && (
        <form className="form" onSubmit={submit} noValidate>
          <FormError error={error} />
          <PasswordField
            label="Nova senha"
            autoComplete="new-password"
            hint="Pelo menos 8 caracteres."
            error={f.errors.password}
            {...f.bind('password')}
          />
          <PasswordField
            label="Repita a nova senha"
            autoComplete="new-password"
            error={f.errors.confirm}
            {...f.bind('confirm')}
          />
          <Button type="submit" variant="primary" block loading={busy}>
            Salvar nova senha
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
